import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Stack,
  Tooltip,
  Typography
} from '@mui/material'
import { FunctionComponent, MouseEventHandler, useState } from 'react'

import { API_KEY_METADATA_KEY } from 'src/constants'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import DoneIcon from '@mui/icons-material/Done'
import { useTenantsState } from '@frontegg/react'

interface Props {
  isOpen: boolean
  handleClose: MouseEventHandler<HTMLButtonElement> | undefined
}

const ApiKeyDialog: FunctionComponent<Props> = ({ isOpen, handleClose }) => {
  const [copied, setCopied] = useState(false)
  const { activeTenant } = useTenantsState()

  const apiKey = JSON.parse(activeTenant?.metadata || '{}')[API_KEY_METADATA_KEY]

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(apiKey)
      setCopied(true)
      setTimeout(() => setCopied(false), 3000)
    } catch (err) {
      console.error('Failed to copy API key: ', err)
    }
  }

  return (
    <Dialog
      open={isOpen}
      onClose={handleClose}
      aria-labelledby="dialog-title"
      slotProps={{ backdrop: { style: { backgroundColor: `rgba(0, 0, 0, 0.2)` } } }}
    >
      <DialogTitle sx={{ pt: 2, pb: 1 }} id="dialog-title">
        <Typography fontWeight={500} fontSize="20px" color="text.primary">
          API Key for {activeTenant?.name}
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Alert severity="info" sx={{ mt: 1, mb: 2 }}>
          Make sure you have the correct tenant selected
        </Alert>

        <Typography variant="body2" fontSize="14px">
          All API requests must be identified by the use of a Developer App Key. This key is secret
          to the developer and must be added to each request’s header section identified by name
          “X-Api-Key”.
        </Typography>
        <Divider sx={{ p: 1 }} />
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="center"
          spacing={1}
          sx={{ pt: 2 }}
        >
          <Typography variant="body2" fontSize="14px">
            {apiKey}
          </Typography>
          {copied ? (
            <Tooltip title="Copied!" placement="top">
              <IconButton disabled sx={{ minWidth: '30px' }}>
                <DoneIcon sx={{ height: '20px', color: 'success.main' }} />
              </IconButton>
            </Tooltip>
          ) : (
            <Tooltip title="Copy to clipboard" placement="top">
              <IconButton onClick={handleCopy} sx={{ minWidth: '30px' }}>
                <ContentCopyIcon sx={{ height: '20px', color: 'primary.main' }} />
              </IconButton>
            </Tooltip>
          )}
        </Stack>
        <Divider sx={{ pb: 2 }} />
      </DialogContent>
      <DialogActions>
        <Button
          onClick={handleClose}
          size="medium"
          sx={{ textTransform: 'uppercase', fontSize: '14px' }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default ApiKeyDialog
